import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Fire, ShieldCheck } from "@phosphor-icons/react";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      sessionStorage.setItem("viva_pending_email", email.toLowerCase());
      toast.success("Verification code sent to your email");
      navigate("/verify");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand / image panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-[#09090B] p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/5953827/pexels-photo-5953827.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10">
          <div className="font-heading font-black text-3xl tracking-tighter text-white">
            <span className="text-primary">VIVA</span> HOME APPLIANCES
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-2">Raw Material Management App</div>
        </div>
        <div className="relative z-10 space-y-4">
          <div className="flex items-center gap-3 text-zinc-300">
            <Fire size={22} weight="fill" className="text-primary" />
            <span className="text-sm">Electric Ovens · 12L / 18L / 25L</span>
          </div>
          <p className="font-heading text-4xl font-black uppercase tracking-tighter text-white leading-none">
            Track every gram <br /> of raw material.
          </p>
          <p className="text-sm text-zinc-400 max-w-sm">
            Precise inventory, stock corrections with full audit trail, and production-linked consumption.
          </p>
        </div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Admin Access</p>
            <h1 className="font-heading text-4xl font-black uppercase tracking-tighter text-zinc-900">Sign In</h1>
            <p className="text-sm text-zinc-500 mt-2">
              We'll email a one-time code to verify it's really you.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-5 bg-white border border-zinc-300 p-8 hard-shadow">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider">Email address</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@vivaappliances.com"
                className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider h-11 transition-colors duration-100"
            >
              {loading ? "Sending code..." : "Continue"}
            </Button>
            <div className="flex items-center gap-2 text-xs text-zinc-400 pt-1">
              <ShieldCheck size={16} weight="bold" /> Protected by email OTP verification
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
